import { Avatar, Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CSSProperties } from '@mui/material/styles';
import {
  Edit,
  Nurse,
  Pharmacist,
  StarRating,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  Button,
  LabelValueColumn,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import EmailIcon from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/EmailIcon';
import { Phone } from '@mui/icons-material';
import React from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

interface CareNavigatorProfileCardProps {
  imageUrl?: string;
  name: string;
  careNavRole: string;
  email: string;
  contact: string;
  rating: number;
  starColor: string;
  details: {
    label: string;
    value: string | React.ReactNode;
    showTooltip?: boolean;
  }[];
  detailsStyles?: CSSProperties;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
   
}

const CareNavigatorProfileCard: React.FC<CareNavigatorProfileCardProps> = ({
  imageUrl,
  name,
  careNavRole,
  email,
  contact,
  rating,
  starColor,
  details,
  setOpen,
}) => {
  const { classes } = makeStyles()({
    root: {
      marginTop: '1rem',
      width: '100%',
      borderRadius: '4px',
      overflow: 'hidden',
      position: 'relative',
    },
    gradient: {
      height: '42px',
      width: '100%',
      background: 'linear-gradient(46.53deg, #C4CEF9 100%, #7D81C3 100%)',
    },
    content: {
      width: '100%',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      boxShadow: '0px 2px 4px 0px #0000000D',
      padding: '1rem 2rem',
    },
    hr: {
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    titleContainer: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '-1.5rem',
    },
    avatar: {
      height: '165px',
      width: '165px',
      fontSize: '48px',
      lineHeight: '48px',
      fontWeight: 'bold',
      marginRight: '10px',
      backgroundColor: patientAccessTheme.palette.primary[300],
      borderRadius: '4px',
      top: '-40px',
    },
    nameDetails: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    details: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    icon: {
      height: '22px',
      width: '22px',
    },
    detailsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '1rem',
    },
    editIcon: {
      height: '22px',
      width: '22px',
      '& path': {
        fill: patientAccessTheme.palette.primary.main,
      },
    },
  })();

  const getAvatarInitial = (name: string) => {
    return name?.charAt(0).toUpperCase();
  };

  const getTruncatedLabelValue = (value: string, isValue: boolean) => {
    const useStyles = makeStyles()({
      ellipses: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        width: '100px',
        whiteSpace: 'nowrap',
      },
      tooltip: {
        backgroundColor: patientAccessTheme.palette.grey[900],
        padding: '10px',
        fontSize: '13px',
        fontFamily: 'Inter',
        fontWeight: 400,
        lineHeight: '16px',
        color: patientAccessTheme.palette.status.white,
        width: 'fit-content',
      },
      arrow: {
        color: patientAccessTheme.palette.grey[900],
        marginLeft: '50px',
      },
      popper: {
        marginTop: '-8px',
        marginLeft: '-20px',
      },
    });

    const { classes: classes1 } = useStyles();
    return (
      <Tooltip
        arrow
        title={value}
        classes={{
          tooltip: classes1.tooltip,
          arrow: classes1.arrow,
          popper: classes1.popper,
        }}
        placement="top-start"
      >
        <Grid>
          {isValue ? (
            <Typography
              fontSize="16px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
              className={classes1.ellipses}
            >
              {value}
            </Typography>
          ) : (
            <Typography
              fontSize="16px"
              lineHeight="22px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              className={classes1.ellipses}
            >
              {value}
            </Typography>
          )}
        </Grid>
      </Tooltip>
    );
  };

  return (
    <Grid container className={classes.root}>
      <Grid className={classes.gradient}></Grid>
      <Grid className={classes.content}>
        <Grid className={classes.titleContainer}>
          <Avatar src={imageUrl} className={classes.avatar}>
            {!imageUrl && getAvatarInitial(name)}
          </Avatar>
          <Grid className={classes.nameDetails}>
            <Grid className={classes.details}>
              <Typography
                fontSize="22px"
                lineHeight="36px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                {name}
              </Typography>
              {careNavRole === 'Nurse' ? (
                <Typography
                  fontSize="22px"
                  lineHeight="36px"
                  fontType="medium"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  className={classes.details}
                >
                  ( {careNavRole}{' '}
                  <Nurse
                    className={classes.icon}
                    style={{ marginLeft: '0.5rem' }}
                  />
                  )
                </Typography>
              ) : (
                <Typography
                  fontSize="22px"
                  lineHeight="36px"
                  fontType="medium"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  className={classes.details}
                >
                  ( {careNavRole}{' '}
                  <Pharmacist
                    className={classes.icon}
                    style={{ marginLeft: '0.5rem' }}
                  />
                  )
                </Typography>
              )}

              <Button
                variant="outlined"
                label="Edit Details"
                startIcon={<Edit className={classes.editIcon} />}
                height="34px"
                width="150px"
                styleProps={{ marginLeft: 'auto' }}
                onClick={() => {
                  setOpen && setOpen(true);
                }}
              />
            </Grid>

            <Grid className={classes.details}>
              <EmailIcon
                className={classes.icon}
                style={{ marginBottom: '-0.5rem' }}
              />
              <Typography
                fontSize="14px"
                lineHeight="22px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.lowemphasis}
              >
                {email}
              </Typography>
              <Phone
                className={classes.icon}
                htmlColor={patientAccessTheme.palette.grey[500]}
              />
              <Typography
                fontSize="14px"
                lineHeight="22px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.lowemphasis}
              >
                {contact}
              </Typography>
            </Grid>

            <StarRating
              value={rating}
              showRating={
                <Grid className={classes.details}>
                  <Typography
                    fontSize="14px"
                    lineHeight="22px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.lowemphasis}
                  >
                    {rating?.toFixed(1)}
                  </Typography>
                  <Typography
                    fontSize="14px"
                    lineHeight="22px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.lowemphasis}
                  >
                    (Overall Rating)
                  </Typography>
                </Grid>
              }
              size={'medium'}
              precision={0.1}
              color={starColor}
              readOnly={true}
            />
          </Grid>
        </Grid>
        <hr className={classes.hr} />
        <Grid className={classes.detailsContainer}>
          {details?.map((item: any) => {
            return (
              <LabelValueColumn
                label={
                  item.showTooltip ? (
                    getTruncatedLabelValue(item.label, false)
                  ) : (
                    <Typography
                      fontSize="16px"
                      lineHeight="22px"
                      fontType="medium"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                    >
                      {item.label}
                    </Typography>
                  )
                }
                value={
                  item.showTooltip ? (
                    getTruncatedLabelValue(item.value, true)
                  ) : (
                    <Typography
                      fontSize="16px"
                      lineHeight="22px"
                      fontType="medium"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                    >
                      {item.value}
                    </Typography>
                  )
                }
                labelStyleProps={{ marginBottom: '4px' }}
                rootStyleProps={{ width: 'fit-content', marginTop: '14px' }}
              />
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CareNavigatorProfileCard;
